// DatabaseManagementTraining.tsx

import React, { useState } from 'react'; // Import useState
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

// Import icons for the 'Why Choose Us' section (similar to Digital Marketing page)
import { FaLaptopCode, FaCertificate, FaBriefcase, FaFileAlt, FaComments, FaUsers } from 'react-icons/fa';

import JoinNowFormPopup from "../../componants/JoinNowFormPopup"; // Assuming this path is correct

// Placeholder imports for database-related images/logos
// You'll need to replace these with actual paths to your images
//  import dbHeroImg from "../../assets/hero.jpg"; // A new hero image for database
import mysqlLogo from "../../assets/Internship/mysql.png"; // Example: path to MySQL logo
import postgresqlLogo from "../../assets/Internship/postgreSql.png"; // Example: path to PostgreSQL logo
import mongodbLogo from "../../assets/Internship/mongodb.png"; // Example: path to MongoDB logo
import sqlServerLogo from "../../assets/Internship/sqlserver.png"; // Example: path to SQL Server logo
import oracleLogo from "../../assets/Internship/oracle.png"; // Example: path to Oracle logo
import datawarehousingLogo from "../../assets/Internship/Data_Analytics_logo.jpg"; // Example: path for Data Warehousing concept


import processImg from "../../assets/Internship/our-process.webp"; // Using the same process image for now

// Animation variants (reusing from Digital Marketing page)
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const DatabaseManagementTraining: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
  const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title

  const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
    setSelectedCourseTitle(courseTitle); // Set the selected course title
    setIsPopupOpen(true); // Open the popup
  };

  const handleClosePopup = () => { // Function to close the popup
    setIsPopupOpen(false); // Close the popup
    setSelectedCourseTitle(""); // Clear selected course when closing
  };

  const whyChooseUsBenefits = [
    {
      icon: <FaLaptopCode className="text-orange-400 text-5xl mb-4" />,
      title: "Hybrid Mode Class",
      description: "Learn flexibly with combined online and offline classroom training sessions.",
    },
    {
      icon: <FaCertificate className="text-orange-400 text-5xl mb-4" />,
      title: "Internship Certificate",
      description: "Receive an internship certificate for hands-on industry project experience.",
    },
    {
      icon: <FaBriefcase className="text-orange-400 text-5xl mb-4" />,
      title: "100% Placement Assistance",
      description: "Complete placement support provided until you secure a suitable job.",
    },
    {
      icon: <FaFileAlt className="text-orange-400 text-5xl mb-4" />,
      title: "CV Building & ATS Score",
      description: "Optimize your resume for ATS and improve shortlisting chances significantly.",
    },
    {
      icon: <FaComments className="text-orange-400 text-5xl mb-4" />,
      title: "Mock Interviews",
      description: "Practice with mock interviews to boost real interview performance confidence.",
    },
    {
      icon: <FaUsers className="text-orange-400 text-5xl mb-4" />,
      title: "15 Students Per Batch",
      description: "Small batches ensure personalized attention and better student-teacher interaction.",
    },
  ];


  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased">
      <Helmet>
        <title>Database Management Training in Noida | Appwars Technologies</title>
        <meta name="description" content="Master Database Management with comprehensive industrial training in Noida. Learn SQL, NoSQL (MySQL, PostgreSQL, MongoDB), Database Design, Administration, and Optimization. 100% placement, hybrid classes, and internship certificates by Appwars Technologies." />
        <meta name="keywords" content="database management, SQL, NoSQL, MySQL, PostgreSQL, MongoDB, database design, database administration, DBA, performance tuning, data warehousing, Noida, training, placement, internship, Appwars Technologies, Nadezhda Ventures" />
        <meta name="author" content="Appwars Technologies / Nadezhda Ventures" />
        <meta property="og:title" content="Database Management Training in Noida | Appwars Technologies" />
        <meta property="og:description" content="Boost your career with comprehensive Database Management training programs in Noida." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="http://yourwebsite.com/database-management-noida" /> {/* Replace with your actual website URL */}
        <link rel="canonical" href="http://yourwebsite.com/database-management-noida" /> {/* Replace with your actual canonical URL */}
      </Helmet>


      <main>
        {/* SECTION 1 - Hero Section with Transparent Image and Text Overlay */}
        <section className="relative overflow-hidden flex items-center justify-center h-[400px] md:h-[400px] lg:h-[400px]">
          {/* Transparent Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://ntier.in/img/database_banner.jpg" // Using a generic technology background image
              alt="Database Management Transparent Background"
              className="w-full h-full object-cover opacity-80" // Adjust opacity for desired transparency
            />
            {/* Optional: Add a subtle overlay for better text readability and darker background */}
            <div className="absolute inset-0 bg-black opacity-70"></div> {/* Adjust opacity as needed */}
          </div>

          {/* Text and Image Content */}
          <div className="min-h-screen flex flex-col md:flex-row items-center justify-center gap-12 px-6 py-20 max-w-7xl mx-auto relative z-10">
            <motion.div
              className="flex-1 text-center md:text-left"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <h1 className="text-4xl md:text-5xl font-extrabold text-orange-500 mb-6">
                Database Management Training
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Master the art of storing, managing,  
                applications with Nadezhda Venture's comprehensive training program.
              </p>

              <button
                className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                onClick={() => handleOpenPopup("Database Management Training")} // Pass the course title
              >
                Join Now !
              </button>
            </motion.div>

            {/* <motion.div
              className="flex-1 w-full max-w-2xl flex justify-center items-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
            >
              <img
                src={dbHeroImg} 
                alt="Database Management Showcase"
                className="rounded-xl shadow-lg w-full h-auto object-contain max-h-[400px]"
              />
            </motion.div> */}
          </div>
        </section>


        {/* Key Database Technologies & Concepts Section */}
        <section className="px-6 md:px-20 py-24 bg-gray-300 text-orange-600">
          <h2 className="text-5xl font-bold text-center mb-10">Key Database Technologies & Concepts</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {/* MySQL */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={mysqlLogo} alt="MySQL Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">MySQL</h3>
              <button
                onClick={() => handleOpenPopup("MySQL Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* PostgreSQL */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={postgresqlLogo} alt="PostgreSQL Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">PostgreSQL</h3>
              <button
                onClick={() => handleOpenPopup("PostgreSQL Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* MongoDB */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={mongodbLogo} alt="MongoDB Icon" className="w-18 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">MongoDB (NoSQL)</h3>
              <button
                onClick={() => handleOpenPopup("MongoDB Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* SQL Server */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={sqlServerLogo} alt="SQL Server Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">SQL Server</h3>
              <button
                onClick={() => handleOpenPopup("SQL Server Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Oracle */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={oracleLogo} alt="Oracle Icon" className="w-18 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Oracle Database</h3>
              <button
                onClick={() => handleOpenPopup("Oracle Database Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Data Warehousing */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={datawarehousingLogo} alt="Data Warehousing Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Data Warehousing</h3>
              <button
                onClick={() => handleOpenPopup("Data Warehousing Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

          </div>
        </section>

        {/* Why Choose Us Section (from image_ab7e7d.png, styling adapted) */}
        <section className="px-6 md:px-20 py-16 md:py-24 bg-white text-gray-800">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Why Choose Us for Database Training?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUsBenefits.map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 bg-blue-950 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                {benefit.icon}
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>


        {/* Our Interview Process Section */}
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-orange-500">Our Training & Placement Process</h2>
        <img
          src={processImg} // Path to your process image file
          alt="Our Training and Placement Process"
          className="w-full h-auto object-cover"
        />

      </main>

      {/* Floating Contact Us button */}
      {/* <a
        href="#"
        className="fixed bottom-8 right-8 bg-green-500 text-white text-lg font-semibold py-3 px-6 rounded-full shadow-xl hover:bg-green-600 transition-colors duration-300 z-50 transform hover:scale-105"
        onClick={(e) => {
          e.preventDefault(); // Prevent default link behavior
          handleOpenPopup("General Database Management Inquiry"); // Open with a general inquiry specific to Digital Marketing
        }}
      >
        Contact Us
      </a> */}

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default DatabaseManagementTraining;